import styled from 'styled-components';
import { ifProp, palette, theme } from 'styled-tools';

import { FontWeights, minWidth, Text } from '@calm-web/design-system';

import pauseIcon from 'icons/pause-filled.svg';
import playIcon from 'icons/play-filled.svg';

export const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	max-width: 1920px;
	padding: 10px 0;
`;

export const TitleContainer = styled.div<{ $isPageHeading?: boolean }>`
	align-items: flex-start;
	display: flex;
	gap: 16px;
	justify-content: space-between;
	margin-bottom: ${ifProp('$isPageHeading', 16, 14)}px;
`;

export const HeaderText = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
`;

// The playlist header replaces TitleContainer for `pack_type: 'playlist'` packs only —
// stacked (art above text) below tablet, side-by-side from tablet up, matching how
// ContentCard/CardGrid switch layout at the same breakpoint rather than introducing a new one.
// margin-bottom of 20px matches Android's pack_vertical_space — the confirmed spacing
// between a pack's own header content and the section below it.
export const PlaylistHeaderWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-bottom: 20px;
	max-width: 1920px;

	${minWidth('tablet')} {
		align-items: center;
		flex-direction: row;
		gap: 24px;
	}
`;

export const CoverArtWrapper = styled.div`
	border-radius: 12px;
	flex-shrink: 0;
	height: 120px;
	overflow: hidden;
	position: relative;
	width: 120px;

	${minWidth('desktop')} {
		border-radius: 16px;
		height: 160px;
		width: 160px;
	}
`;

// Capped at mobileLarge, same as PlaylistControls below — the whole text column (title,
// curator link, controls, track summary) stays mobile-proportioned rather than stretching
// wide next to the cover art on larger screens.
export const PlaylistInfo = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 8px;
	max-width: 480px;
	min-width: 0;
`;

export const PlaylistTitle = styled(Text).attrs({
	el: 'h2',
	styleAs: 'h4',
	color: 'white',
	weight: FontWeights.Demi,
})`
	${minWidth('desktop')} {
		font-size: 28px;
		line-height: 36px;
	}
`;

export const TrackSummaryText = styled(Text).attrs({
	el: 'p',
	styleAs: 'h6',
})`
	color: ${palette('whiteAlpha70')};
	font-size: 13px;
	// Text's <p> element carries its own default bottom margin, which stacked on top of
	// PlaylistHeaderWrapper's own margin-bottom and widened the gap to the row list below
	// past what that wrapper alone specifies. This is the last element in the header, so
	// the wrapper's margin should be the only source of that gap.
	margin-bottom: 0;
`;

// Capped at mobileLarge (480px) — the largest phone-sized viewport — and staying capped at
// every wider breakpoint too, so Play/Shuffle keep the ~50/50 mobile proportions this header
// is meant to match rather than growing wider as the page does.
export const PlaylistControls = styled.div`
	display: flex;
	gap: 12px;
	margin-top: 4px;
	max-width: 480px;
	width: 100%;
`;

const playlistControlButtonCSS = `
	align-items: center;
	border: none;
	border-radius: 999px;
	cursor: pointer;
	display: flex;
	font-size: 16px;
	font-weight: 500;
	gap: 8px;
	padding: 15px 20px;
	transition: opacity ${theme('animation.transition')};

	&:hover {
		opacity: 0.8;
	}

	&:disabled {
		cursor: default;
		opacity: 0.4;
	}
`;

// Play is the primary action for the whole playlist — solid fill, same treatment as the
// page's own "Try Calm for Free" CTA — while Shuffle stays the secondary/translucent style.
export const PlayButton = styled.button`
	${playlistControlButtonCSS}
	background: ${palette('white')};
	color: rgba(0, 0, 0, 0.85);
	flex: 1;
	justify-content: center;
`;

export const ShuffleButton = styled.button`
	${playlistControlButtonCSS}
	background: rgba(255, 255, 255, 0.12);
	color: ${palette('white')};
	flex: 1;
	justify-content: center;
`;

export const PauseIcon = styled(pauseIcon)`
	flex-shrink: 0;
	height: 14px;
	width: 14px;

	& path {
		fill: rgba(0, 0, 0, 0.85);
	}
`;

export const PlayIcon = styled(playIcon)`
	flex-shrink: 0;
	height: 14px;
	width: 14px;

	& path {
		fill: rgba(0, 0, 0, 0.85);
	}
`;

// No shuffle icon exists in this app's icon set — drawn directly as a stroke-based SVG
// (the standard crossing-arrows glyph) rather than adding a one-off asset for a single use.
export const ShuffleIcon = styled.svg.attrs({
	viewBox: '0 0 24 24',
	xmlns: 'http://www.w3.org/2000/svg',
	fill: 'none',
})`
	flex-shrink: 0;
	height: 14px;
	stroke: ${palette('white')};
	stroke-linecap: round;
	stroke-linejoin: round;
	stroke-width: 2;
	width: 14px;
`;

export const Title = styled(Text).attrs({
	el: 'h2',
	color: 'white',
	noMargin: true,
})`
	font-size: 18px;
	font-weight: 500;
`;

export const PageTitle = styled(Text).attrs({
	el: 'h1',
	color: 'white',
	noMargin: true,
})`
	font-size: 28px;
	font-weight: 600;
	line-height: normal;
`;

export const Subtitle = styled(Text).attrs({
	el: 'p',
	color: 'white',
	noMargin: true,
})`
	font-size: 16px;
	font-weight: 400;
	max-width: 600px;
`;

export const PlaylistDescription = styled(Text).attrs({
	el: 'p',
	color: 'white',
	noMargin: true,
})`
	font-size: 16px;
	font-weight: 400;
	margin-top: 4px;
`;

export const RowWrapper = styled.div`
	align-items: center;
	display: flex;
	position: relative;
`;

export const RowContainer = styled.a`
	align-items: center;
	display: flex;
	flex: 1;
	gap: 12px;
	min-width: 0;
	text-decoration: none;
	transition: opacity ${theme('animation.transition')};

	&:hover {
		opacity: 0.8;
	}
`;

export const ThumbnailWrapper = styled.div<{ $isCircle: boolean }>`
	border-radius: ${p => (p.$isCircle ? '50%' : '12px')};
	flex-shrink: 0;
	height: 80px;
	overflow: hidden;
	position: relative;
	width: 80px;
`;

// Pinned to the row's top-trailing corner, not the artwork. An editorial tag gets a white pill;
// the plain "New" fallback gets the dark blur.
export const RowBadge = styled.div<{ $isTag: boolean }>`
	background: ${ifProp('$isTag', palette('white'), 'rgba(0, 0, 0, 0.3)')};
	backdrop-filter: ${ifProp('$isTag', 'none', 'blur(10px)')};
	border-radius: 999px;
	color: ${ifProp('$isTag', palette('black'), palette('white'))};
	font-size: 10px;
	font-weight: 500;
	padding: 4px 6px;
	pointer-events: none;
	position: absolute;
	right: 8px;
	top: 8px;
`;

export const InfoContainer = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
`;

export const RowTitle = styled(Text).attrs({
	el: 'h3',
	styleAs: 'h6',
})`
	color: ${palette('white')};
	font-size: 13px;
	font-weight: 600;
	// Mobile renders row text at the font's natural line height, not the Text default.
	line-height: normal;
	margin: 0;
`;

export const RowLine = styled(Text).attrs({
	el: 'p',
	styleAs: 'h6',
})`
	color: ${palette('white')};
	font-size: 12px;
	font-weight: 400;
	line-height: normal;
	margin: 0;
`;
